import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { loadPdfJs } from '@/lib/utils/extract-pdf-text'

export interface PdfDocumentPreviewProps {
  documentToken: string
  /** Page to scroll into view; changes on a pager click or a deep link. */
  pageNumber: number
  title: string
  /**
   * Called when pdf.js cannot render this document at all — fetch failure,
   * corrupt file, or the very first page failing to render. The caller
   * falls back to the server-rasterized image + extracted text.
   */
  onUnavailable: () => void
  /**
   * Fires as the reader scrolls past a page boundary, so the pager label
   * and prev/next buttons can follow along without a click.
   */
  onVisiblePageChange?: (pageNumber: number) => void
}

type LoadState = 'loading' | 'ready'

// Render at CSS width times the device ratio (capped) so pages stay crisp on
// retina, matching the citation viewer's pattern.
const MAX_RENDER_SCALE = 2

interface PdfPageProxy {
  getViewport: (params: { scale: number }) => { width: number; height: number }
  render: (params: {
    canvas: HTMLCanvasElement
    viewport: unknown
  }) => { cancel: () => void; promise: Promise<void> }
}

interface PdfDocumentProxy {
  destroy: () => Promise<void>
  numPages: number
  getPage: (pageNumber: number) => Promise<PdfPageProxy>
}

/**
 * Renders every page of the ORIGINAL PDF with pdf.js as one continuous,
 * scrollable column — vector-accurate, unlike the server-rasterized JPEG the
 * legacy fallback shows. Two stages:
 *  1. Fetch the document's real bytes as a blob and hand them to pdf.js once
 *     per `documentToken`; also reads pdf.js's own page count and page-1
 *     aspect ratio, which the page placeholders use before they render.
 *  2. Each page canvas renders lazily, the first time it scrolls near the
 *     viewport, and reports itself as "visible" once it's actually on
 *     screen so the pager label can track scroll position instead of only
 *     reacting to clicks.
 */
export function PdfDocumentPreview(props: PdfDocumentPreviewProps) {
  const { documentToken, pageNumber, title, onUnavailable, onVisiblePageChange } = props

  const [state, setState] = useState<LoadState>('loading')
  const [totalPages, setTotalPages] = useState<number | null>(null)
  const [pageAspectRatio, setPageAspectRatio] = useState<number | null>(null)
  const [renderedPages, setRenderedPages] = useState<ReadonlySet<number>>(new Set())

  const containerRef = useRef<HTMLDivElement | null>(null)
  const pageElsRef = useRef(new Map<number, HTMLDivElement>())
  const canvasElsRef = useRef(new Map<number, HTMLCanvasElement>())
  const renderStartedRef = useRef(new Set<number>())
  const pdfRef = useRef<PdfDocumentProxy | null>(null)
  const lastReportedPageRef = useRef<number | null>(null)
  const onUnavailableRef = useRef(onUnavailable)
  onUnavailableRef.current = onUnavailable
  const onVisiblePageChangeRef = useRef(onVisiblePageChange)
  onVisiblePageChangeRef.current = onVisiblePageChange

  // Stage 1: fetch the real bytes, parse the document, and read its page
  // count + first-page aspect ratio. Runs once per `documentToken`.
  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    setState('loading')
    setTotalPages(null)
    setPageAspectRatio(null)
    setRenderedPages(new Set())
    pdfRef.current = null
    renderStartedRef.current = new Set()
    lastReportedPageRef.current = null

    void (async () => {
      try {
        const [{ downloadUrl }, pdfjsLib] = await Promise.all([
          knowledgeApi.downloadDocument(documentToken),
          loadPdfJs(),
        ])
        if (cancelled) {
          if (
            downloadUrl?.startsWith('blob:') &&
            typeof URL.revokeObjectURL === 'function'
          ) {
            URL.revokeObjectURL(downloadUrl)
          }
          return
        }
        if (!downloadUrl) {
          throw new Error('No download URL for document')
        }
        objectUrl = downloadUrl

        const pdf = (await pdfjsLib.getDocument({ url: downloadUrl })
          .promise) as unknown as PdfDocumentProxy
        if (cancelled) {
          void pdf.destroy()
          return
        }
        const firstPage = await pdf.getPage(1)
        const firstViewport = firstPage.getViewport({ scale: 1 })
        if (cancelled) {
          void pdf.destroy()
          return
        }
        pdfRef.current = pdf
        setTotalPages(Math.max(1, pdf.numPages))
        setPageAspectRatio(
          firstViewport.height > 0
            ? firstViewport.width / firstViewport.height
            : null,
        )
        setState('ready')
      } catch {
        if (!cancelled) {
          onUnavailableRef.current()
        }
      }
    })()

    return () => {
      cancelled = true
      const pdf = pdfRef.current
      pdfRef.current = null
      if (pdf) {
        void pdf.destroy().catch(() => undefined)
      }
      if (
        objectUrl?.startsWith('blob:') &&
        typeof URL.revokeObjectURL === 'function'
      ) {
        URL.revokeObjectURL(objectUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentToken])

  async function renderPage(n: number) {
    if (renderStartedRef.current.has(n)) return
    const pdf = pdfRef.current
    const canvas = canvasElsRef.current.get(n)
    if (!pdf || !canvas) return
    renderStartedRef.current.add(n)
    try {
      const page = await pdf.getPage(n)
      const base = page.getViewport({ scale: 1 })
      if (!base.width) {
        throw new Error('PDF page has no width')
      }
      const cssWidth = canvas.parentElement?.clientWidth || 640
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_RENDER_SCALE)
      const viewport = page.getViewport({
        scale: (cssWidth / base.width) * ratio,
      })
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      await page.render({ canvas, viewport }).promise
      setRenderedPages((prev) => new Set(prev).add(n))
    } catch {
      renderStartedRef.current.delete(n)
      // Only the very first page failing is a doc-level signal — fall back
      // to the legacy renderer. Any later page just stays unrendered rather
      // than discarding every page already on screen.
      if (n === 1) {
        onUnavailableRef.current()
      }
    }
  }

  // Lazily render pages as they scroll near the viewport, and report which
  // page is currently on screen so the pager label can follow scrolling.
  useEffect(() => {
    if (state !== 'ready' || totalPages == null) return
    const scrollRoot = containerRef.current?.closest<HTMLElement>(
      '[data-testid="knowledge-preview-body"]',
    )
    if (!scrollRoot) return

    const renderObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const n = Number((entry.target as HTMLElement).dataset.pdfPage)
          if (n) void renderPage(n)
        }
      },
      { root: scrollRoot, rootMargin: '150% 0px' },
    )

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!visible) return
        const n = Number((visible.target as HTMLElement).dataset.pdfPage)
        if (n && n !== lastReportedPageRef.current) {
          lastReportedPageRef.current = n
          onVisiblePageChangeRef.current?.(n)
        }
      },
      { root: scrollRoot, threshold: 0.5 },
    )

    pageElsRef.current.forEach((el) => {
      renderObserver.observe(el)
      visibilityObserver.observe(el)
    })

    return () => {
      renderObserver.disconnect()
      visibilityObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, totalPages])

  // External navigation (a pager click or a deep link) changed `pageNumber`
  // without us having reported it ourselves — render it eagerly (it may be
  // far outside the lazy-render window, e.g. a deep link) and scroll it into
  // view. Guarding on `lastReportedPageRef` keeps this from fighting
  // scroll-driven updates.
  useEffect(() => {
    if (totalPages == null || lastReportedPageRef.current === pageNumber) return
    const clamped = Math.min(Math.max(1, pageNumber), totalPages)
    lastReportedPageRef.current = clamped
    void renderPage(clamped)
    pageElsRef.current.get(clamped)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, totalPages])

  const pageNumbers = useMemo(
    () => (totalPages ? Array.from({ length: totalPages }, (_, i) => i + 1) : []),
    [totalPages],
  )

  return (
    <div
      ref={containerRef}
      className="mx-auto flex w-full max-w-3xl flex-col gap-6"
      data-testid="knowledge-doc-pdf-page"
    >
      {state === 'loading' ? (
        <div
          className="flex items-center justify-center gap-2 py-16"
          role="status"
          aria-live="polite"
          data-testid="knowledge-doc-pdf-loading"
        >
          <Loader2
            className="h-4 w-4 animate-spin text-document-page-muted"
            aria-hidden
          />
          <Text size="sm" className="text-document-page-muted">
            Loading document…
          </Text>
        </div>
      ) : (
        pageNumbers.map((n) => (
          <div
            key={n}
            ref={(el) => {
              if (el) pageElsRef.current.set(n, el)
              else pageElsRef.current.delete(n)
            }}
            data-pdf-page={n}
            className="rounded-xl border border-card-border bg-document-page p-6 text-document-page-foreground shadow-sm"
            data-testid={`knowledge-doc-pdf-page-${n}`}
          >
            <div
              className="relative w-full"
              style={pageAspectRatio ? { aspectRatio: pageAspectRatio } : undefined}
            >
              <canvas
                ref={(el) => {
                  if (el) canvasElsRef.current.set(n, el)
                  else canvasElsRef.current.delete(n)
                }}
                className="absolute inset-0 block h-full w-full"
                role="img"
                aria-label={`${title}, page ${n}`}
                data-testid={`knowledge-doc-pdf-canvas-${n}`}
              />
              {!renderedPages.has(n) ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2
                    className="h-4 w-4 animate-spin text-document-page-muted"
                    aria-hidden
                  />
                </div>
              ) : null}
            </div>
            <div className="mt-4 flex justify-between text-xs text-document-page-muted">
              <span>{title}</span>
              <span>p. {n}</span>
            </div>
          </div>
        ))
      )}
    </div>
  )
}
